/**
 * Wake Lock Hook
 * 
 * React hook for managing screen wake lock in components.
 * Automatically requests/releases wake lock based on active state.
 */

import { useEffect, useState } from 'react';
import WakeLockService, { WakeLockState } from '../services/WakeLockService';
import { logger } from '../utils/logger';

export interface UseWakeLockOptions {
  /**
   * Whether to automatically request wake lock
   * Set to true when session is active
   */
  enabled: boolean;
  
  /**
   * Callback when visibility changes
   */
  onVisibilityChange?: (isVisible: boolean) => void;
  
  /**
   * Callback when wake lock state changes
   */
  onStateChange?: (state: WakeLockState) => void;
}

export function useWakeLock(options: UseWakeLockOptions) {
  const { enabled, onVisibilityChange, onStateChange } = options;
  
  const [state, setState] = useState<WakeLockState>(WakeLockService.getState());
  const [isVisible, setIsVisible] = useState<boolean>(!document.hidden);

  useEffect(() => {
    // Register event listeners
    WakeLockService.on('onStateChange', (newState) => {
      setState(newState);
      onStateChange?.(newState);
    });

    WakeLockService.on('onVisibilityChange', (visible) => {
      setIsVisible(visible);
      onVisibilityChange?.(visible);
    });

    WakeLockService.on('onError', (error) => {
      console.error('[useWakeLock] Error:', error);
    });

    // Cleanup
    return () => {
      WakeLockService.off('onStateChange');
      WakeLockService.off('onVisibilityChange');
      WakeLockService.off('onError');
    };
  }, [onVisibilityChange, onStateChange]);

  // Request/release wake lock based on enabled state
  useEffect(() => {
    if (enabled) {
      logger.debug('[useWakeLock] Session active - requesting wake lock');
      WakeLockService.request();
    } else {
      logger.debug('[useWakeLock] Session inactive - releasing wake lock');
      WakeLockService.release();
    }
  }, [enabled]);

  return {
    state,
    isVisible,
    isSupported: WakeLockService.isWakeLockSupported(),
    request: () => WakeLockService.request(),
    release: () => WakeLockService.release(),
  };
}
